import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
    getHseDiaryCalendarApi,
    getDiaryDictTreeApi,
    getDiaryByDateApi,
    getDiaryByIdApi,
    postDiaryCommentApi,
    putDiaryCommentApi,
    postEventDataApi,
    postExposureDataApi,
    postIncidentDataApi,
    getEventDataApi,
    getExposureDataApi,
    getIncidentDataApi,
    postCardDataApi,
    getCardDataApi,

} from 'api'
import { BoundarySlice } from 'redux/reducers'

const initialState = {
    calendarInfo: null, // 被点击的日历信息
    calendarData: null, // 日历上当前范围内显示的总结填写信息
    dictData: null, // 数据字典
    listData: null, // 回显数据
    locationName: '', // location 名称
    locationId: '', // location id
    save: false,
    id: '',
}

/* 获取日历上的日数据 */
export const getHseDiaryCalendarAction = createAsyncThunk(
    'HseDiarySlice/getHseDiaryCalendarAction',
    async (params, thunkAPI) => {
        try {
            const { data } = await getHseDiaryCalendarApi(params)
            return data.data
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 获取数据字典 */
export const getDiaryDictDataAction = createAsyncThunk(
    'HseDiarySlice/getDiaryDictDataAction',
    async (dictType, thunkAPI) => {
        const cardDataDict = getDiaryDictTreeApi(dictType[0]) // 事件
        const eventDataDict = getDiaryDictTreeApi(dictType[1]) // 事件
        const companyDataDict = getDiaryDictTreeApi(dictType[2]) // 单位
        const personnelDataDict = getDiaryDictTreeApi(dictType[3]) // 人员类型
        const exposureHoursDataDict = getDiaryDictTreeApi(dictType[4]) // 暴露类型
        const incidentDataDict = getDiaryDictTreeApi(dictType[5]) // 事故类型
        return Promise.all([cardDataDict, eventDataDict, companyDataDict, personnelDataDict, exposureHoursDataDict, incidentDataDict])
            .then(dictData => dictData)
          
            .catch(error => thunkAPI.dispatch(BoundarySlice.actions.addError(error)))
    }
)

/* 通过日期获取 id */
export const getDiaryByDateAction = createAsyncThunk(
    'HseDiarySlice/getDiaryByDateAction',
    async (listDataParams, thunkAPI) => {
        try {
            const commentData = await getDiaryByDateApi(listDataParams) // 通过日期查询 id
            return commentData?.data?.data?.id
        } catch (error) {
            thunkAPI.dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 获取数据 */
export const getDiaryDataAction = createAsyncThunk('HseDiarySlice/getDiaryDataAction', async (id, thunkAPI) => {
    const commentData = getDiaryByIdApi(id) // 通过 id 查询
    const cardData = getCardDataApi(id)
    const eventData = getEventDataApi(id)
    const exposureData = getExposureDataApi(id)
    const incidentData = getIncidentDataApi(id)
    return Promise.all([commentData, cardData, eventData, exposureData, incidentData])
        .then(listData => listData)
        .catch(error => thunkAPI.dispatch(BoundarySlice.actions.addError(error)))
})

/* 保存数据 */
export const postDiaryDataAction = createAsyncThunk(
    'HseDiarySlice/postDiaryDataAction',
    async ({ id, commentParams, cardParams, eventParams, exposurParams, incidentParams }, { dispatch }) => {
        let vid
        try {
            if (!id) {
                const commentData = await postDiaryCommentApi(commentParams) // 新增
                vid = commentData.data.data.id
                await postCardDataApi(cardParams, vid)
                await postEventDataApi(eventParams, commentData.data.data.id)
                await postExposureDataApi(exposurParams, commentData.data.data.id)
                await postIncidentDataApi(incidentParams, commentData.data.data.id)
                dispatch(HseDiarySlice.actions.addId(vid))
                return { isTrue: true, id: vid } // 已保存状态
            } else {
                vid = id
                await putDiaryCommentApi(commentParams, id) // 修改
                await postCardDataApi(cardParams, id)
                await postEventDataApi(eventParams, id)
                await postExposureDataApi(exposurParams, id)
                await postIncidentDataApi(incidentParams, id)
                return { isTrue: true, id } // 已保存状态
            }
        } catch (error) {
            dispatch(HseDiarySlice.actions.addId(vid))
            dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

/* 保存 周/月 数据 */
export const postDiaryWeekDataAction = createAsyncThunk(
    'HseDiarySlice/postDiaryDataAction',
    async ({ id, commentParams }, { dispatch }) => {
        let vid
        try {
            if (!id) {
                const commentData = await postDiaryCommentApi(commentParams) // 新增
                vid = commentData.data.data.id
                dispatch(HseDiarySlice.actions.addId(vid))
                return { isTrue: true, id: vid } // 已保存状态
            } else {
                vid = id
                await putDiaryCommentApi(commentParams, id) // 修改
                return { isTrue: true, id } // 已保存状态
            }
        } catch (error) {
            dispatch(HseDiarySlice.actions.addId(vid))
            dispatch(BoundarySlice.actions.addError(error))
        }
    }
)

export const HseDiarySlice = createSlice({
    name: 'HseDiarySlice',
    initialState,
    reducers: {
        /* 日历点击信息 */
        addCalendarInfo: (state, action) => {
            state.calendarInfo = action.payload
        },
        /* 清空日历与详情页信息 */
        clearListData: state => {
            state.listData = null
            state.calendarData = null
            state.save = false
            state.id = ''
        },
        /* 当前日历主 id */
        addId: (state, action) => {
            state.id = action.payload
        },
        /* location 名称 */
        addLocationName: (state, action) => {
            state.locationName = action.payload
        },
        /* location id */
        addLocationId: (state, action) => {
            state.locationId = action.payload
        },
    },
    extraReducers: {
        /* 获取日历上的数据 */
        [getHseDiaryCalendarAction.fulfilled.type]: (state, action) => {
            state.calendarData = action.payload
        },

        /* 处理数据字典 */
        [getDiaryDictDataAction.fulfilled.type]: (state, action) => {
            state.dictData = action.payload
        },

        /* 提交总结数据 */
        [postDiaryDataAction.fulfilled.type]: (state, action) => {
            state.save = action.payload
        },

        /* 获取回显数据 */
        [getDiaryDataAction.fulfilled.type]: (state, action) => {
            state.listData = action.payload
        },

        /* 通过日期，获取 id */
        [getDiaryByDateAction.fulfilled.type]: (state, action) => {
            state.id = action.payload
        },
    },
})
